import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import emailjs from '@emailjs/browser';

// --- SERVICE CONFIGURATION ---
// Replace these placeholders with your actual EmailJS dashboard keys
const EMAILJS_SERVICE_ID = 'service_a78ejgg';
const EMAILJS_TEMPLATE_ID = 'template_5u76hwr';
const EMAILJS_PUBLIC_KEY = 'x0u-DnU5a5xkq1RQH';

// --- TYPES & DATA SCHEMA FOR EASY UPDATES ---

export interface ProductItem {
  id: string;
  name: string;
  subtitle?: string;
  image: string;
  description?: string;
}

export interface FreeGift {
  title: string;
  description?: string;
  image?: string;
}

export interface OfferDetails {
  id: string;
  title: string;
  subtitle: string;
  heroImage: string;
  price: string;
  deliveryFee: string;
  deliveryInfo: string;
  contactPhone: string;
  comboProducts: ProductItem[];
  freeGifts: FreeGift[];
}

// Data store / Configuration for offers
export const OFFERS_DATA: Record<string, OfferDetails> = {
  '1': {
    id: '1',
    title: 'USK 3 in 1 Welding Combo Offer',
    subtitle: '250 Amps MMA Inverter Welding Machine with Angle Grinder and Impact Drill',
    heroImage: 'https://scontent.fnbo1-1.fna.fbcdn.net/v/t39.30808-6/778767484_122113164776640985_6373094169450069661_n.jpg?stp=dst-jpg_tt6&cstp=mx921x1152&ctp=s640x640&_nc_cat=103&ccb=1-7&_nc_sid=833d8c&_nc_eui2=AeHzhVhrV-Kll778exFPM7r9naXpBOF5MhKdpekE4XkyEgd4V1irxTelY-cynt2-KoE4bNBELvTdXa3OyTEmSQqP&_nc_ohc=R-PNLkYdr08Q7kNvwGvxlvu&_nc_oc=AdpOi2MUB2Cg3QNQbPHmVbXXxZPg7k5-Pj8qtpBLDNpExMADOATpeYtppOKl2l5Mjgs&_nc_zt=23&_nc_ht=scontent.fnbo1-1.fna&_nc_gid=oWVc2v2QRvbMJcOLq9dAMg&_nc_ss=7b2a8&oh=00_AQEfPVj3gx0-IOXN6m8opPrqyaeTDR2hEnMZlBBJ04lqAA&oe=6A8E3AF0',
    price: 'Ksh. 14,599',
    deliveryFee: 'Ksh. 300 to Ksh. 750 (Depending on location)',
    deliveryInfo: 'Same day delivery within Nairobi & environs for orders placed before 4:00 PM. Next day delivery outside Nairobi County.',
    contactPhone: '+254 700 870225',
    comboProducts: [
      {
        id: 'welder',
        name: '250A MMA Inverter Welding Machine',
        image: 'https://ke.jumia.is/unsafe/fit-in/300x300/filters:fill(white)/product/58/5568723/1.jpg?0339',
      },
      {
        id: 'cables',
        name: 'Welding Holders And Cables',
        image: 'https://topworldelectronics.co.ke/wp-content/uploads/2026/03/61JQal1oiL._AC_UF10001000_QL80_-768x808.jpg',
      },
      {
        id: 'grinder',
        name: 'Angle Grinder (Power House)',
        image: 'https://topworldelectronics.co.ke/wp-content/uploads/2026/07/bosch-angle-grinder-66c5053e94a79-1024x1024.avif',
      },
      {
        id: 'drill',
        name: 'Impact Drill 700W',
        image: 'https://topworldelectronics.co.ke/wp-content/uploads/2026/07/1.webp',
      },
    ],
    freeGifts: [
      {
        title: 'Double-Head Screwdriver Bit Set (PH2, 100mm)',
        description: 'Strong steel, magnetic tips, ideal for power drills',
        image: 'https://topworldelectronics.co.ke/wp-content/uploads/2026/03/Fixtec-Working-Safety-Mechanic-C-1.jpg',
      },
      {
        title: 'Protective Coofix Welding Goggles',
        image: 'https://topworldelectronics.co.ke/wp-content/uploads/2026/03/hsgw01__05149-removebg-preview.png',
      },
      {
        title: 'Protective Working Gloves',
        description: 'Improved grip & hand safety',
        image: 'https://topworldelectronics.co.ke/wp-content/uploads/2026/06/Orange-overalls-removebg-preview.png',
      },
    ],
  },
};

// --- REACT ROUTER COMPONENT ---

export default function OfferLandingPage() {
  const { id } = useParams<{ id: string }>();
  const offerId = id || '1';
  const offer = OFFERS_DATA[offerId];

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    location: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <h1 className="text-2xl font-bold text-red-600">Offer Not Found</h1>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    // Dynamic variables mapping to your EmailJS template fields
    const templateParams = {
      to_email: 'sales@eagleskylinesolutions.co.ke, eagle.skyline.solutions@gmail.com',
      offer_id: offer.id,
      offer_title: offer.title,
      price: offer.price,
      customer_name: formData.name,
      customer_phone: formData.phone,
      delivery_location: formData.location,
      submitted_at: new Date().toLocaleString(),
    };

    try {
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      if (response.status === 200) {
        setSubmitted(true);
      } else {
        throw new Error('Failed to dispatch order notification.');
      }
    } catch (err: any) {
      console.error('EmailJS Submission Error:', err);
      setSubmitError(
        err.text || 'Failed to send your order. Please check your connection or call us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="max-w-2xl mx-auto bg-white min-h-screen pb-16 font-sans text-gray-800 shadow-md">
      
      {/* SECTION 1: HERO BANNER */}
      <section className="relative w-full">
        <img
          src={offer.heroImage}
          alt={offer.title}
          className="w-full h-auto object-cover"
        />
      </section>

      {/* SECTION 2: OFFER HEADLINE */}
      <section className="p-4 text-center bg-gray-50 border-b border-gray-200">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
          {offer.title}
        </h1>
        <p className="text-base sm:text-lg text-blue-700 font-semibold">
          {offer.subtitle}
        </p>
      </section>

      {/* SECTION 3: INCLUDED COMBO PRODUCTS */}
      <section className="p-4 space-y-6">
        <h2 className="text-xl font-bold text-center text-gray-900 border-b pb-2">
          What’s Included in This Combo
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {offer.comboProducts.map((product) => (
            <div
              key={product.id}
              className="flex flex-col items-center bg-gray-50 p-4 rounded-xl border border-gray-100"
            >
              <img
                src={product.image}
                alt={product.name}
                className="w-48 h-48 object-contain mb-3"
              />
              <h3 className="font-bold text-center text-gray-800 text-base">
                {product.name}
              </h3>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 4: FREE GIFTS */}
      <section className="p-4 bg-orange-50 border-y border-orange-200 my-4">
        <h2 className="text-2xl font-black text-center text-orange-600 mb-4 flex items-center justify-center gap-2">
          <span>🎁</span> FREE GIFTS INCLUDED
        </h2>

        <div className="space-y-4 mb-6">
          {offer.freeGifts.map((gift, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <span className="text-green-600 font-bold text-lg">✅</span>
              <div>
                <strong className="text-gray-900 font-bold">{gift.title}</strong>
                {gift.description && (
                  <p className="text-sm text-gray-600">{gift.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {offer.freeGifts.map((gift, idx) =>
            gift.image ? (
              <img
                key={idx}
                src={gift.image}
                alt={gift.title}
                className="w-full h-24 object-contain rounded-lg bg-white p-1 border"
              />
            ) : null
          )}
        </div>
      </section>

      {/* SECTION 5: PRICING & DELIVERY INFO */}
      <section className="p-4 space-y-4 text-center">
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
          <p className="text-2xl font-black text-blue-900">
            Offer Price: {offer.price}
          </p>
          <p className="text-sm text-gray-700 mt-1">
            Delivery Fee: {offer.deliveryFee}
          </p>
        </div>

        <div className="text-sm text-gray-600 space-y-2 border-t pt-4">
          <h3 className="font-bold text-gray-900 text-base">Delivery Information</h3>
          <p>{offer.deliveryInfo}</p>
          <div className="pt-2">
            <span className="block text-xs uppercase tracking-wider text-gray-500 font-semibold">
              Reach Us Directly:
            </span>
            <a
              href={`tel:${offer.contactPhone}`}
              className="text-lg font-extrabold text-green-600 hover:underline"
            >
              {offer.contactPhone}
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 6: ORDER FORM */}
      <section id="order-form" className="p-4 bg-gray-50 border-t border-gray-200">
        <h3 className="text-xl font-bold text-center text-gray-900 mb-4">
          Fill Your Details Below to Place Your Order
        </h3>

        {submitted ? (
          <div className="p-6 bg-green-100 text-green-800 rounded-xl text-center font-bold">
            🎉 Order Received! We have dispatched your order details to sales@eagleskylinesolutions.co.ke and will call you shortly.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {submitError && (
              <div className="p-3 bg-red-100 border border-red-300 text-red-700 text-sm rounded-lg">
                {submitError}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="07XX XXX XXX"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Delivery Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Town / Estate / Building"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-black text-lg rounded-xl shadow-lg transition-transform active:scale-95 flex items-center justify-center gap-2"
            >
              <span>🚚</span>
              {isSubmitting ? 'Sending Order...' : 'Submit Order'}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}